import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { MdSearch } from 'react-icons/md';
import { FaTags } from 'react-icons/fa';

import {
  InputSearch,
  ButtonLink,
  HeaderBackground,
  HeaderPage,
  HeaderInfo,
  Table,
  Pagination,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Position,
  Button,
  Loader,
  TableResponsive,
  PopUpMenu,
  Can,
} from 'components';

import { ModalHandles } from 'components/Modal';

import api from 'services/api';
import { useToast } from 'contexts/ToastProvider';

import * as S from './styles';

interface Rls {
  id: string;
  name: string;
  slug: string;
}

interface Paginate {
  data: Rls[];
  page: number;
  per_page: number;
  last_page: number;
  total: number;
}

const RlsList: React.FC = () => {
  const [rls, setRls] = useState<Paginate>();
  const [selectedRls, setSelectedRls] = useState<Rls>();
  const modalRemoverRef = useRef<ModalHandles>(null);

  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const { addToast } = useToast();
  const history = useHistory();

  const handleSearch = useCallback(
    async ({ page, per_page }) => {
      try {
        setLoading(true);

        const params = {
          page,
          per_page,
          term,
        };

        const response = await api.get<Paginate>('/rls', { params });

        const { data } = response;

        setRls(data);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Ops...',
          description: 'Ops, ocorreu um erro no processo de pesquisa',
        });
      } finally {
        setLoading(false);
      }
    },
    [term, addToast],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 10,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = async (): Promise<void> => {
    try {
      await api.delete(`/rls/${selectedRls?.id}`);

      if (!rls) return;
      const newList = rls.data.filter((item) => item.id !== selectedRls?.id);

      setRls({ ...rls, data: newList });

      modalRemoverRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Rls removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao inserir um novo registro',
      });
    }
  };

  const handleOpenModalRemove = useCallback((categorySelected: Rls): void => {
    setSelectedRls(categorySelected);
    modalRemoverRef.current?.openModal();
  }, []);

  return (
    <S.Container>
      <HeaderBackground>
        <HeaderPage>
          <HeaderInfo title="RLS" icon={FaTags} />

          <S.ContainerSearch>
            <InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter'
                  ? handleSearch({ page: 1, per_page: rls?.per_page })
                  : {}
              }
            />
          </S.ContainerSearch>

          <div>
            <Can checkAccess="rls_create">
              <ButtonLink title="Adicionar rls" to="/rls/add">
                Adicionar
              </ButtonLink>
            </Can>
          </div>
        </HeaderPage>
      </HeaderBackground>

      <S.Panel>
        {loading ? (
          <Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <TableResponsive>
              <Table>
                <thead>
                  <tr>
                    <th className="text-left">Rls</th>
                    <th className="text-left">Slug</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {rls &&
                    rls.data.map((item: Rls) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.slug}</td>
                        <td className="text-center">
                          <PopUpMenu>
                            <Can checkAccess="rls_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(`/rls/edit/${item.id}`)
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </Can>

                            <Can checkAccess="rls_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModalRemove(item)}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </Can>
                          </PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </TableResponsive>
            <S.FooterContainer>
              {rls && (
                <Pagination
                  page={rls?.page}
                  total={rls?.total}
                  per_page={rls?.per_page}
                  last_page={rls?.last_page}
                  onSearch={handleSearch}
                />
              )}
            </S.FooterContainer>
          </>
        )}
      </S.Panel>

      <Modal ref={modalRemoverRef} options={{ width: '400px' }}>
        <ModalHeader>
          <ModalTitle>Você Confirma a EXCLUSÃO?</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p>O registro será removido completamente!</p>
        </ModalBody>
        <ModalFooter>
          <Position align="right">
            <Button
              variant="outline-secondary"
              onClick={() => modalRemoverRef.current?.closeModal()}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => handleRemove()}
              style={{ marginLeft: '16px' }}
            >
              SIM, confirmo a exclusão
            </Button>
          </Position>
        </ModalFooter>
      </Modal>
    </S.Container>
  );
};

export default RlsList;
